// Pure data helpers for the Analytics page (VisualizationPage). Moved out of the page
// unchanged so they can be unit tested; the page's numbers depend on them.

import { DEFAULT_BE_THRESHOLD } from "../../statsUtils";
import { cleanCreatedTimestamp } from "../../utils/dateUtils";
import { isTapeReading } from "../../utils/tradeHelpers";
import { computePerformanceSnapshot, getDurationBucket } from "../../utils/performanceInsights";
import { normalizeTradeEnvValueForAnalytics } from "../../utils/tradeEnvAnalytics";
import { matchesMindsetFactor } from "../../utils/mindsetAnalytics";

export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const SESSION_OPTIONS = ["pre-market", "NY-AM", "NY-Lch", "NY-PM", "Asia", "London"];

export function getEntryDateMs(entry) {
  if (entry.created) {
    const ts = cleanCreatedTimestamp(entry.created);
    const t = new Date(ts).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (entry.year != null && entry.month != null && entry.day != null) {
    const y = parseInt(entry.year, 10);
    const m = parseInt(entry.month, 10) - 1;
    const d = parseInt(entry.day, 10);
    const t = new Date(y, m, d).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

export function getEntryDate(entry) {
  if (entry.year != null && entry.month != null && entry.day != null) {
    const y = parseInt(entry.year, 10);
    const m = parseInt(entry.month, 10) - 1;
    const d = parseInt(entry.day, 10);
    return new Date(y, m, d);
  }
  if (entry.created) {
    const ts = cleanCreatedTimestamp(entry.created);
    return new Date(ts);
  }
  return null;
}

export function getDayOfWeek(entry) {
  const d = getEntryDate(entry);
  if (!d || Number.isNaN(d.getTime())) return null;
  const idx = d.getDay();
  return DAY_NAMES[(idx + 6) % 7];
}

export function getEntryHourValue(entry) {
  const hour = Number.parseInt(String(entry?.entryTime || "").split(":")[0], 10);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? String(hour) : "";
}

export function matchesFactorFilter(entry, factorFilter) {
  if (!factorFilter?.field || !factorFilter?.value) return true;
  if (factorFilter.field === "duration") return getDurationBucket(entry) === factorFilter.value;
  if (factorFilter.field.startsWith("mindsetExecution.")) {
    return matchesMindsetFactor(entry, factorFilter.field, factorFilter.value);
  }
  return String(entry[factorFilter.field] || "").trim().toLowerCase() === String(factorFilter.value).trim().toLowerCase();
}

export function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function findBestGroup(trades, getKey, minimumSample = 3, metric = "expectancy", beThreshold = DEFAULT_BE_THRESHOLD) {
  const groups = new Map();
  trades.forEach((trade) => {
    const key = getKey(trade);
    if (!key) return;
    const current = groups.get(key) || { label: key, trades: [] };
    current.trades.push(trade);
    groups.set(key, current);
  });
  return [...groups.values()]
    .map((group) => ({ ...group, ...computePerformanceSnapshot(group.trades, beThreshold) }))
    .filter((group) => group.count >= minimumSample)
    .sort((a, b) => {
      const aValue = metric === "profitFactor" ? (a.profitFactor ?? -Infinity) : (a[metric] ?? -Infinity);
      const bValue = metric === "profitFactor" ? (b.profitFactor ?? -Infinity) : (b[metric] ?? -Infinity);
      return bValue - aValue;
    })[0] || null;
}

export function isTradeEntry(e) {
  if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) return false;
  const p = e.pnl;
  if (p === undefined || p === null || p === "") return false;
  return !Number.isNaN(Number(p));
}

/** Apply a filter snapshot to a trade list. Used for current filters and for preset comparison. */
export function applyFilters(list, filters, fieldDefinitions, selectedAccountId) {
  if (!filters) return [...list];
  let out = [...list];
  const {
    accountFilter = "current",
    dateFrom = "",
    dateTo = "",
    dayOfWeekFilter = [],
    sessionFilter = [],
    tradingStyleFilter = "Any",
    tickerFilter = "",
    pnlMin = "",
    pnlMax = "",
    rrMin = "",
    rrMax = "",
    tradeEnvFilters = {},
    entryHourFilter = "",
    factorFilter = null,
  } = filters;

  if (accountFilter === "current" && selectedAccountId) {
    out = out.filter((e) => e.accountId === selectedAccountId);
  } else if (accountFilter !== "all" && accountFilter !== "current") {
    out = out.filter((e) => e.accountId === accountFilter);
  }

  if (dateFrom || dateTo) {
    out = out.filter((e) => {
      const d = getEntryDate(e);
      if (!d || Number.isNaN(d.getTime())) return false;
      if (dateFrom && d < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo) {
        const end = new Date(dateTo + "T23:59:59.999");
        if (d > end) return false;
      }
      return true;
    });
  }

  if (dayOfWeekFilter.length > 0) {
    out = out.filter((e) => {
      const day = getDayOfWeek(e);
      return day && dayOfWeekFilter.includes(day);
    });
  }

  if (sessionFilter.length > 0) {
    out = out.filter((e) => sessionFilter.includes(e.tradeSession || ""));
  }

  if (entryHourFilter !== "") {
    out = out.filter((entry) => getEntryHourValue(entry) === String(entryHourFilter));
  }

  if (tradingStyleFilter !== "Any") {
    out = out.filter((e) => (e.tradingStyle || "").toLowerCase() === tradingStyleFilter.toLowerCase());
  }

  if (tickerFilter.trim()) {
    const t = tickerFilter.trim().toUpperCase();
    out = out.filter((e) => (e.tickerTraded || "").toUpperCase() === t);
  }

  if (pnlMin !== "" && !Number.isNaN(Number(pnlMin))) {
    out = out.filter((e) => (Number(e.pnl) || 0) >= Number(pnlMin));
  }
  if (pnlMax !== "" && !Number.isNaN(Number(pnlMax))) {
    out = out.filter((e) => (Number(e.pnl) || 0) <= Number(pnlMax));
  }
  if (rrMin !== "" && !Number.isNaN(Number(rrMin))) {
    out = out.filter((e) => (Number(e.rr) || 0) >= Number(rrMin));
  }
  if (rrMax !== "" && !Number.isNaN(Number(rrMax))) {
    out = out.filter((e) => (Number(e.rr) || 0) <= Number(rrMax));
  }

  const enabledFields = (fieldDefinitions?.tradeEnv || []).filter((f) => f.enabled);
  enabledFields.forEach((field) => {
    const v = tradeEnvFilters[field.id];
    if (v === "Any" || v === undefined || v === "") return;
    out = out.filter((e) => normalizeTradeEnvValueForAnalytics(e[field.id], field) === v);
  });

  if (factorFilter?.field && factorFilter?.value) {
    out = out.filter((entry) => matchesFactorFilter(entry, factorFilter));
  }

  return out;
}

export function getFilterStateFromPreset(preset) {
  if (!preset?.filters) return { tickerFilter: "", sessionFilter: [], tradingStyleFilter: "Any" };
  const f = preset.filters;
  return {
    tickerFilter: f.tickerFilter ?? "",
    sessionFilter: Array.isArray(f.sessionFilter) ? f.sessionFilter : [],
    tradingStyleFilter: f.tradingStyleFilter ?? "Any",
  };
}

/** Default direction when a trade-table column is first selected. */
export const TRADE_SORT_DEFAULT_DIR = { date: "desc", ticker: "asc", pnl: "desc", rr: "desc", session: "asc" };

/** Sort trades for the matching-trades table (and the CSV export). */
export function sortTrades(trades, sortKey, sortDir) {
  const list = [...trades];
  const mult = sortDir === "asc" ? 1 : -1;
  list.sort((a, b) => {
    let va, vb;
    switch (sortKey) {
      case "date":
        va = getEntryDateMs(a);
        vb = getEntryDateMs(b);
        return mult * (va - vb);
      case "ticker":
        va = (a.tickerTraded || "").toLowerCase();
        vb = (b.tickerTraded || "").toLowerCase();
        return mult * va.localeCompare(vb);
      case "pnl":
        va = Number(a.pnl) || 0;
        vb = Number(b.pnl) || 0;
        return mult * (va - vb);
      case "rr":
        va = Number(a.rr) || 0;
        vb = Number(b.rr) || 0;
        return mult * (va - vb);
      case "session":
        va = (a.tradeSession || "").toLowerCase();
        vb = (b.tradeSession || "").toLowerCase();
        return mult * va.localeCompare(vb);
      default:
        return 0;
    }
  });
  return list;
}

/** Previous period of equal length, or null when the range is incomplete/invalid. */
export function getPreviousPeriodRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return null;
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.round((end - start) / dayMs) + 1;
  const previousEnd = new Date(start.getTime() - dayMs);
  const previousStart = new Date(previousEnd.getTime() - ((days - 1) * dayMs));
  return { dateFrom: formatDateInput(previousStart), dateTo: formatDateInput(previousEnd) };
}
